import assign from 'lodash/assign';
import get from 'lodash/get';
import find from 'lodash/find';
import filter from 'lodash/filter';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import reduce from 'lodash/reduce';
import some from 'lodash/some';
import every from 'lodash/every';
import union from 'lodash/union';
import pull from 'lodash/pull';

import { ProductPickerServiceModule } from 'services/data/productPicker.service';

import template from './productPicker.html';
import './productPicker.scss';

export const ProductPickerModule = angular
    .module('app.components.productPicker', [
        'ngMaterial',
        ProductPickerServiceModule
    ]).component('productPicker', {
        template,
        bindings: {
            filterItems: '<',
            confirmText: '<',
            filterText: '<',
            showZeroInventory: '<',
            isZeroInventorySelectionEnabled: '<',
            addProductsHeadingKey: '<',
            customHeaderClass: '<',
            customBannerClass: '<',
            gtmId: '<'
        },
        controller: class ProductPicker {
            constructor(
                $filter,
                $mdDialog,
                productPicker
            ) {
                assign(this, {
                    $filter,
                    $mdDialog,
                    productPicker,
                    STEP: {
                        CATEGORY: 1,
                        PRODUCT: 2
                    },
                    items: {
                        categories: [],
                        products: [],
                        filteredProducts: []
                    },
                    productsCountBannerText: '',
                    itemSearch: { data: { name: '' } },
                    selectedCategory: {},
                    selectedItems: [],
                    initCategoryIds: [],
                    initProductIds: [],
                    loading: true
                });
            }

            $onInit() {
                this.step = this.STEP.CATEGORY;
                this.itemSearch.data.name = this.filterText || '';

                this.productPicker.getMinimalProductVariantsAndCategories()
                    .then((items) => {
                        this.setItems(items);
                        if (items.categories.length === 0 || this.filterText.length > 0) {
                            this.step = this.STEP.PRODUCT;
                            this.selectedCategory = {};
                        }
                        this.goToStep(this.step, this.filterText);
                    })
                    .finally(() => { this.loading = false; });
            }

            shouldShowNoResultsForFilterMessage() {
                return this.items.filteredProducts.length === 0
                    && this.itemSearch.data.name.length > 0;
            }

            shouldShowNoProductsMessage() {
                return this.items.filteredProducts.length === 0
                    && this.itemSearch.data.name.length === 0
                    && !this.loading;
            }

            constructCountText(count, countNouns, trailingText = '') {
                const { singularNoun, pluralNoun } = countNouns;
                return `${count} ${count === 1 ? singularNoun : pluralNoun}${trailingText}`;
            }

            setProductsCountBannerText(products = [], categories = []) {
                const categoriesCountTextFragment =
                    this.step === this.STEP.CATEGORY && categories.length > 0
                        ? this.constructCountText(categories.length, {
                            singularNoun: 'category',
                            pluralNoun: 'categories'
                        }, ', ')
                        : '';
                const nonVariantProducts = filter(products, product => !product.isVariant);
                const variantProducts = filter(products, product => product.isVariant || !product.hasVariants);
                const productsCount = nonVariantProducts.length;
                const variantsCount = variantProducts.length;
                const stockCount = reduce(variantProducts, (totalTrackedInventory, p) => {
                    totalTrackedInventory += p.isInventoryTracked ? p.quantity : 0;
                    return totalTrackedInventory;
                }, 0);
                const isInventoryTrackedForAnyProduct = products.find(p => p.isInventoryTracked);
                const stockCountTextFragment = `${stockCount} in stock for`;
                const variantsCountTextFragment = this.constructCountText(variantsCount, {
                    singularNoun: 'variant',
                    pluralNoun: 'variants'
                });
                const productsCountTextFragment = this.constructCountText(productsCount, {
                    singularNoun: 'product',
                    pluralNoun: 'products'
                }, ', ');
                const stockAndVariantsCountTextFragment = isInventoryTrackedForAnyProduct
                    ? `${stockCountTextFragment} ${variantsCountTextFragment}`
                    : variantsCountTextFragment;
                this.productsCountBannerText =
                    `${categoriesCountTextFragment}${productsCountTextFragment}${stockAndVariantsCountTextFragment}`;
            }

            showZeroInventoryChanged() {
                this.goToStep(this.step, this.itemSearch.data.name);
            }

            setItems(items) {
                angular.forEach(items.categories, (category) => {
                    this.items.categories.push({
                        selected: false,
                        data: category,
                        id: category.id
                    });

                    if (this.initCategoryIds.indexOf(category.id) !== -1) {
                        this.selectItem(this.items.categories[this.items.length - 1]);
                    }
                });

                angular.forEach(items.products, (product) => {
                    const hasVariants = product.productVariants.length > 1;
                    if (!hasVariants) {
                        assign(product, pick(product.productVariants[0], [
                            'sku',
                            'price',
                            'priceOverride',
                            'isInventoryTracked',
                            'quantity',
                            'variants'
                        ]));
                    }
                    if ((hasVariants && !this.allVariantsExcluded(product)) || !this.isProductDisabled(product)) {
                        const p = cloneDeep(product);
                        const quantity = reduce(p.productVariants, (sum, variant) => {
                            return sum + variant.quantity;
                        }, 0);
                        const isInventoryTracked = p.productVariants[0].isInventoryTracked;
                        if (p.pricing && p.pricing.salePrice) {
                            p.price = p.pricing.salePrice;
                        }
                        const isOutOfStock = !hasVariants && isInventoryTracked && quantity === 0;
                        this.items.products.push({
                            selected: false,
                            data: p,
                            id: p.id,
                            hasVariants: hasVariants,
                            isInventoryTracked,
                            quantity,
                            isOutOfStock,
                            disabled: isOutOfStock && !this.isZeroInventorySelectionEnabled
                        });
                    }

                    if (hasVariants) {
                        product.productVariants.map((variant) => {
                            const p = cloneDeep(product);
                            assign(p, pick(variant, [
                                'sku',
                                'price',
                                'priceOverride',
                                'isInventoryTracked',
                                'quantity',
                                'variants'
                            ]));
                            if (product.pricing && product.pricing.salePrice) {
                                p.price = product.pricing.salePrice;
                            }
                            p.productVariantId = variant.id;
                            if (!this.isProductDisabled(variant, true)) {
                                const isOutOfStock = variant.isInventoryTracked && variant.quantity === 0;
                                this.items.products.push({
                                    selected: false,
                                    data: p,
                                    id: p.id,
                                    isVariant: true,
                                    isOutOfStock,
                                    isInventoryTracked: variant.isInventoryTracked,
                                    quantity: variant.quantity,
                                    disabled: isOutOfStock && !this.isZeroInventorySelectionEnabled
                                });
                            }
                            return 0; // lint needs a return value from an arrow function
                        });
                    }

                    if (this.initProductIds.indexOf(product.id) !== -1) {
                        this.selectItem(this.items.products[this.items.length - 1]);
                    }
                });
            }

            allVariantsExcluded(product) {
                return every(product.productVariants, (variant) => {
                    return find(this.filterItems, { product: { productVariantId: variant.id } });
                });
            }

            isProductDisabled(product, isVariant) {
                return !!find(this.filterItems, (item) => {
                    const itemProduct = item.product || item;
                    return isVariant
                        ? (itemProduct.productVariantId === product.id)
                        : (itemProduct.id === product.id);
                });
            }

            selectItem(item) {
                if (item.disabled) return;

                item.selected = true;
                if (item.isVariant) {
                    // if all variants of a product are checked, also check the parent
                    const parent = this.getParentProduct(item);
                    if (this.areAllVariantsSelected(parent)) this.selectItem(parent);
                    this.selectedItems = union(this.selectedItems, [item.data.productVariantId]);
                } else {
                    // don't add product id if it has variants
                    if (!item.hasVariants && this.selectedItems.indexOf(item.data.id) === -1) {
                        this.selectedItems = union(this.selectedItems, [item.data.id]);
                    }
                }
            }

            selectCategory(category) {
                this.selectedCategory = category.data;
                this.goToStep(this.STEP.PRODUCT);
            }

            onFilterTextChange() {
                this.goToStep(this.STEP.PRODUCT, this.itemSearch.data.name);
            }

            onFilterTextCleared() {
                this.goToStep(this.STEP.PRODUCT);
            }

            goToStep(step, filterText) {
                this.step = step;
                this.itemSearch.data.name = filterText || '';
                if (step === this.STEP.CATEGORY) {
                    this.selectedCategory = {};
                    this.selectedItems = [];
                    this.items.filteredProducts = this.items.products;
                }

                if (step === this.STEP.PRODUCT) {
                    this.filterProducts();
                    this.items.filteredProducts = this.$filter('filter')(this.filteredProducts, this.itemSearch);
                }

                if (!this.showZeroInventory) {
                    this.items.filteredProducts = filter(this.items.filteredProducts, (p) => {
                        return !p.isOutOfStock;
                    });
                }
                this.setProductsCountBannerText(this.items.filteredProducts, this.items.categories);
            }

            getCategoryName(id) {
                if (!id) return;
                const categoryWithId = find(
                    this.items.categories,
                    category => category.data.categoryId === id
                );
                return get(categoryWithId, 'data.name');
            }

            getParentProduct(item) {
                return filter(this.items.products, { id: item.id, hasVariants: true })[0];
            }

            filterProducts() {
                this.filteredProducts = this.selectedCategory.id ?
                    filter(this.items.products, (product) => {
                        return get(product, 'data.categoryIds', []).indexOf(this.selectedCategory.id) > -1;
                    }) :
                    this.items.products;
            }

            deselectItem(item) {
                item.selected = false;
                const itemId = item.isVariant ? item.data.productVariantId : item.data.id;
                pull(this.selectedItems, itemId);
            }

            toggleItem(item) {
                if (item.disabled) return;

                if (item.selected) {
                    this.deselectItem(item);
                    if (item.hasVariants) {
                        this.toggleVariants(item, false);
                    } else if (item.isVariant) {
                        const itemParent = this.getParentProduct(item);
                        if (!this.areVariantsSelected(itemParent)) {
                            this.deselectItem(itemParent);
                        }
                    }
                } else {
                    this.selectItem(item);
                    if (item.hasVariants) {
                        this.toggleVariants(item, true);
                    } else if (item.isVariant) {
                        const itemParent = this.getParentProduct(item);
                        if (this.areAllVariantsSelected(itemParent)) {
                            this.selectItem(itemParent);
                        }
                    }
                }
            }

            areVariantsSelected(item) {
                if (!item.hasVariants) return false;
                return some(filter(
                    this.filteredProducts, { id: item.id, isVariant: true }
                ), { selected: true });
            }

            areAllVariantsSelected(item) {
                if (!item.hasVariants) return false;
                return every(filter(
                    this.filteredProducts, { id: item.id, isVariant: true, disabled: false }
                ), { selected: true });
            }

            toggleVariants(item, checked) {
                filter(this.filteredProducts, { id: item.id, isVariant: true })
                    .forEach((variant) => {
                        if (checked) this.selectItem(variant);
                        else this.deselectItem(variant);
                    });
            }

            deselectAll() {
                angular.forEach(this.items.products, (item) => {
                    item.selected = false;
                });
                this.selectedItems = [];
            }

            hasSelectedItems() {
                return this.selectedItems.length > 0;
            }

            close() {
                this.$mdDialog.cancel();
            }

            confirm() {
                let items = angular.copy(this.items.products);
                let selectedItems = [];
                angular.forEach(items, (item) => {
                    if (item.selected && !item.hasVariants) {
                        delete item.selected;
                        delete item.hovering;
                        delete item.$$hashKey;
                        selectedItems.push(item);
                    }
                });
                this.$mdDialog.hide(selectedItems);
            }

            getIndentation(index) {
                const iconPadding = 54;

                if (index === 0) {
                    return 0;
                }

                if (index === 1) {
                    return `${iconPadding}px`;
                }

                const lineWidth = 25;
                const spacing = 8;
                const baseIndentation = iconPadding + lineWidth + spacing;
                const multiplier = index - 2;

                return `${baseIndentation + ((lineWidth + spacing) * multiplier)}px`;
            }
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/components/productPicker/productPicker.component.js